<template>
  <div class="gc-wrapper">
    <label class="header-device">Grupos de comparação</label>
    <p class="gc-help">
      Vincule dispositivos que devem ser vistos juntos (ex.: os medidores de um mesmo biodigestor).
      No mapa, o aside do dispositivo ganha o botão <b>comparar</b>.
    </p>

    <div v-if="loading" class="gc-empty">Carregando…</div>
    <template v-else>
      <div v-if="!grupos.length" class="gc-empty">Este dispositivo não está em nenhum grupo.</div>
      <div v-for="grupo in grupos" :key="grupo.id" class="gc-grupo">
        <div class="gc-grupo-info">
          <b>{{ grupo.nome }}</b>
          <div class="gc-chips">
            <span v-for="d in grupo.dispositivos" :key="d.qrCodeId"
              :class="['gc-chip', { 'gc-chip-atual': d.qrCodeId === deviceId }]">
              {{ d.nome }}
            </span>
          </div>
        </div>
        <i class="tf-icons bx bx-trash text-danger gc-remover" title="Tirar este dispositivo do grupo"
          @click="remover(grupo)"></i>
      </div>

      <div class="gc-add">
        <select class="form-select" v-model="grupoSelecionado">
          <option :value="null" disabled>Adicionar a um grupo…</option>
          <option v-for="g in gruposDisponiveis" :key="g.id" :value="g.id">{{ g.nome }}</option>
          <option value="novo">+ Criar novo grupo</option>
        </select>
        <input v-if="grupoSelecionado === 'novo'" type="text" class="form-control" maxlength="120"
          placeholder="Nome do grupo (ex.: Biodigestor 1)" v-model="novoNome" />
        <button type="button" class="btn btn-success gc-btn" :disabled="!podeAdicionar || saving" @click="adicionar">
          {{ saving ? 'Salvando…' : 'Adicionar' }}
        </button>
      </div>
    </template>
    <Alert ref="alert"></Alert>
  </div>
</template>

<script>
import Alert from "../Alert.vue";

/**
 * Vínculo do dispositivo com grupos de comparação (só admin, por enquanto). Salva na
 * hora (não depende do "salvar" do formulário), porque o vínculo vive numa tabela à
 * parte. O grupo criado fica no nome do dono do dispositivo, não do admin.
 */
export default {
  name: "GruposComparacaoField",
  components: { Alert },
  props: {
    deviceId: { type: Number, required: true },
  },
  data() {
    return {
      grupos: [],
      // Grupos do mesmo cliente (dono do dispositivo) em que ele ainda não está.
      disponiveis: [],
      grupoSelecionado: null,
      novoNome: "",
      loading: false,
      saving: false,
    };
  },
  computed: {
    gruposDisponiveis() {
      return this.disponiveis;
    },
    podeAdicionar() {
      if (this.grupoSelecionado === "novo") return this.novoNome.trim().length > 0;
      return this.grupoSelecionado != null;
    },
  },
  watch: {
    deviceId: {
      handler() { this.carregar(); },
      immediate: true,
    },
  },
  methods: {
    async carregar() {
      if (!this.deviceId) return;
      this.loading = true;
      try {
        const [doDispositivo, disponiveis] = await Promise.all([
          this.urlGet(`/GrupoComparacao/Dispositivo/${this.deviceId}`),
          this.urlGet(`/GrupoComparacao/Disponiveis/${this.deviceId}`),
        ]);
        this.grupos = doDispositivo.data || [];
        this.disponiveis = disponiveis.data || [];
      } catch (e) {
        this.grupos = [];
        this.disponiveis = [];
      } finally {
        this.loading = false;
      }
    },
    async adicionar() {
      if (!this.podeAdicionar) return;
      this.saving = true;
      try {
        if (this.grupoSelecionado === "novo") {
          await this.urlPost(`/GrupoComparacao`, { nome: this.novoNome.trim(), qrCodeId: this.deviceId });
        } else {
          await this.urlPost(`/GrupoComparacao/${this.grupoSelecionado}/Dispositivo/${this.deviceId}`, {});
        }
        this.grupoSelecionado = null;
        this.novoNome = "";
        await this.carregar();
      } catch (e) {
        this.erro(e);
      } finally {
        this.saving = false;
      }
    },
    async remover(grupo) {
      try {
        await this.urlDelete(`/GrupoComparacao/${grupo.id}/Dispositivo/${this.deviceId}`);
        await this.carregar();
      } catch (e) {
        this.erro(e);
      }
    },
    erro(e) {
      const msg = e?.response?.data?.message || e?.response?.data?.Message || "Não foi possível salvar o grupo.";
      this.$refs.alert.show("Erro", msg, null, 3000, "bg-danger");
    },
  },
};
</script>

<style scoped>
.gc-wrapper {
  margin: 20px 0;
}
.gc-help {
  font-size: 13px;
  color: #777;
  margin: 4px 0 10px;
}
.gc-empty {
  font-size: 13px;
  color: #999;
  text-align: center;
  margin: 8px 0;
}
.gc-grupo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid #e3e3e3;
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 6px;
}
.gc-grupo-info {
  min-width: 0;
}
.gc-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.gc-chip {
  font-size: 12px;
  background: #F2F2F2;
  border-radius: 20px;
  padding: 2px 10px;
}
.gc-chip-atual {
  background: #E8F8E6;
  font-weight: 600;
}
.gc-remover {
  cursor: pointer;
  flex: 0 0 auto;
}
.gc-add {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
}
.gc-add .form-select,
.gc-add .form-control {
  flex: 1 1 200px;
}
.gc-btn {
  background-color: #006035;
}
</style>
